import { useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MetricCard, MetricCardGrid } from "@/components/admin/MetricCard";
import { useAdminStats } from "@/hooks/use-admin-stats";
import { useAdminAuditLog } from "@/hooks/use-admin-audit-log";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserCheck,
  Clock,
  AlertTriangle,
  Activity,
  Shield,
} from "lucide-react";
import { format } from "date-fns";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { stats, loading: statsLoading, fetchStats } = useAdminStats();
  const { actions, loading: actionsLoading, fetchActions } = useAdminAuditLog();

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchStats(),
        fetchActions(undefined, 1),
      ]);
    };
    loadData();
  }, [fetchStats, fetchActions]);

  const formatActionType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <AdminLayout
      title="Dashboard"
      description="Platform administration overview"
    >
      {/* User Stats Grid */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">User Status</h3>
        <MetricCardGrid columns={4}>
          <MetricCard
            title="Total Users"
            value={stats?.total_users ?? 0}
            icon={Users}
            loading={statsLoading}
            onClick={() => navigate("/admin/users")}
          />
          <MetricCard
            title="Active Users"
            value={stats?.active_users ?? 0}
            icon={UserCheck}
            variant="success"
            loading={statsLoading}
            onClick={() => navigate("/admin/users?status=active")}
          />
          <MetricCard
            title="Trial Users"
            value={stats?.trial_users ?? 0}
            icon={Clock}
            loading={statsLoading}
            onClick={() => navigate("/admin/users?status=trial")}
          />
          <MetricCard
            title="Pending Approval"
            value={stats?.pending_users ?? 0}
            icon={Shield}
            variant={(stats?.pending_users ?? 0) > 0 ? "warning" : "default"}
            loading={statsLoading}
            onClick={() => navigate("/admin/users?status=pending_approval")}
          />
        </MetricCardGrid>
      </div>

      {/* Quick Actions */}
      {(stats?.pending_users ?? 0) > 0 && (
        <Card className="mb-8 border-orange-500/50 bg-orange-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-500">
              <AlertTriangle className="h-5 w-5" />
              Action Required
            </CardTitle>
            <CardDescription>
              {stats?.pending_users} user(s) awaiting activation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/admin/users?status=pending")}>
              Review Pending Users
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recent Admin Activity */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent Admin Activity</CardTitle>
            <CardDescription>Last 10 admin actions</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/admin/audit-log")}>
            View All
          </Button>
        </CardHeader>
        <CardContent>
          {actionsLoading ? (
            <div className="text-center text-muted-foreground py-4">Loading...</div>
          ) : actions.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">
              No admin actions recorded yet
            </div>
          ) : (
            <div className="space-y-3">
              {actions.slice(0, 10).map((action) => (
                <div
                  key={action.id}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium text-sm">
                        {formatActionType(action.action)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {action.user_name || action.user_id.slice(0, 8)}...
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">
                      {action.ip_address || "N/A"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(action.created_at), "MMM d, HH:mm")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
